import { useCallback, useMemo } from "react";
import { MultiPolygon, Polygon, Point } from "@turf/turf";
import { feature } from "@turf/helpers";
import { useSnapshot } from "valtio";
import { refreshSiteGeometryStore } from "src/js/stores/siteGeometry/actions/refreshSiteGeometryStore";
import { updateMapSettings } from "src/js/stores/map/actions/updateMapSettings";
import { mapStore, SelectionFeature, SelectionGeometry, SelectionSource, SelectionType } from "src/js/stores/map/store";
import { useSiteStagesById } from "react-migration/domains/sites/hooks/useSiteStages";
import {
  SaveSiteButton,
  SaveSiteButtonProps,
} from "react-migration/domains/sites/components/SaveSiteButton/SaveSiteButton";
import { SaveSiteLayer } from "react-migration/domains/sites/typings/SaveSiteLayer";
import { cleanTypename } from "react-migration/lib/util/cleanTypename";
import { CreatedSite } from "react-migration/domains/sites/components/SaveSiteButton/useSaveSiteMutation/useSaveSiteMutation";

type WorkbenchSaveSiteButtonProps = Pick<SaveSiteButtonProps, "onCompleted" | "fullWidth"> & {
  selection: SelectionFeature;
  setSelection(selection?: SelectionFeature | undefined): void;
  onSave?: () => void;
  buttonText?: string;
  cardName?: string;
  expanded?: boolean;
};

export function WorkbenchSaveSiteButton({
  selection,
  setSelection,
  onSave,
  cardName,
  expanded = false,
  ...saveSiteButtonProps
}: WorkbenchSaveSiteButtonProps) {
  const savableGeometry = useMemo(() => getSavableGeometry(selection), [selection]);

  const stagesById = useSiteStagesById();
  const { googleMap } = useSnapshot(mapStore);
  const { mergeSiteOnSave } = selection || {};

  const onSaveHandler = useCallback(
    (savedSite: CreatedSite) => {
      const savedSiteId = savedSite?._id;
      const savedSiteStageId = savedSite?._stage!._id;
      const savedSiteGeom = cleanTypename(savedSite?.geometry as SelectionGeometry);

      if (!savedSiteId || !savedSiteGeom) return;

      googleMap && refreshSiteGeometryStore(googleMap);

      setSelection({
        type: SelectionType.SITE,
        id: savedSiteId,
        feature: feature(savedSiteGeom),
        savable: false,
        __source: SelectionSource.SAVE_SITE_BUTTON,
      });

      if (stagesById[savedSiteStageId]?.hidden) {
        updateMapSettings("showHiddenSites", true);
      }

      onSave?.();
    },
    [googleMap, setSelection, stagesById, onSave]
  );

  if (!savableGeometry) return null;

  let selectionTypeToLog = selection.type;

  if (selectionTypeToLog === SelectionType.DESIGNATION) {
    selectionTypeToLog = selection.feature?.properties?.sub_category_id;
  }

  return (
    <SaveSiteButton
      source="Workbench"
      selectionType={selectionTypeToLog}
      // TODO it seems this is vestigal but required, it is used on the backend
      layer={SaveSiteLayer.Constraints}
      siteGeometry={savableGeometry}
      data-testid="save-site-button"
      handleSaveSites={([site]) => onSaveHandler(site)}
      siteMatchType="EXACT"
      mergeSiteOnSave={mergeSiteOnSave}
      text={saveSiteButtonProps.buttonText}
      cardName={cardName}
      expanded={expanded}
      {...saveSiteButtonProps}
    />
  );
}

const isSavableGeometry = (geometry: SelectionGeometry): geometry is Polygon | MultiPolygon | Point =>
  geometry.type === "Polygon" || geometry.type === "MultiPolygon" || geometry.type === "Point";

function getSavableGeometry(selection: SelectionFeature) {
  const savingDisabled = selection.savable === false;
  if (!selection?.feature?.geometry) return undefined;

  const isSavable = !savingDisabled && selection.type;

  if (!isSavable) {
    return undefined;
  }

  const savableGeometry = selection.savableGeometry?.geometry ?? selection.feature?.geometry;

  if (isSavableGeometry(savableGeometry)) {
    return savableGeometry;
  }
}
